import { markdown } from '@codemirror/lang-markdown'
import { languages } from '@codemirror/language-data'
import { EditorView } from '@codemirror/view'
import { tags } from '@lezer/highlight'
import { CaretDownIcon } from '@phosphor-icons/react'
import { mappedColors, useTheme } from '@pixee/design-system'
import { createTheme } from '@uiw/codemirror-themes'
import CodeMirror, { type ReactCodeMirrorRef } from '@uiw/react-codemirror'
import classNames from 'classnames'
import { useEffect, useMemo, useRef, useState } from 'react'
import { RenderedMarkdown } from '../rendered-markdown.tsx'
import { TooltipWithHover } from '../tooltip.tsx'
import * as styles from './markdown-editor.css.ts'

type ViewMode = 'code' | 'preview'

export type MarkdownEditorProps = {
  value: string
  onChange: (value: string) => unknown
  placeholder: string
  readOnly: boolean
  focusToken?: number
  initialViewMode?: ViewMode
}

export const useCodeMirrorTheme = (readOnly: boolean) => {
  const { theme } = useTheme()

  return useMemo(() => {
    const background = readOnly ? mappedColors.neutral.neutral80 : mappedColors.neutral.orbit
    const foreground = mappedColors.neutral.neutral10

    return createTheme({
      theme,
      settings: {
        background,
        foreground,
        caret: foreground,
        selection: mappedColors.neutral.neutral70,
        gutterBackground: 'transparent',
        gutterForeground: mappedColors.neutral.neutral40,
        gutterBorder: 'transparent',
        lineHighlight: 'transparent',
        fontFamily: "'Inconsolata', monospace",
        fontSize: '14px',
      },
      styles: [
        { tag: tags.heading, color: mappedColors.secondary.vapor },
        { tag: tags.emphasis, color: mappedColors.secondary.ether, fontStyle: 'italic' },
        { tag: tags.strong, color: mappedColors.secondary.ether, fontWeight: 'bold' },
        { tag: tags.link, color: mappedColors.primary.drift },
        { tag: tags.comment, color: mappedColors.neutral.neutral40 },
        { tag: tags.processingInstruction, color: mappedColors.neutral.neutral40 },
        { tag: tags.monospace, color: mappedColors.secondary.halcyon },
        { tag: tags.url, color: mappedColors.neutral.neutral40 },
        { tag: tags.quote, color: mappedColors.neutral.neutral30, fontStyle: 'italic' },
        { tag: tags.contentSeparator, color: mappedColors.neutral.neutral50 },
        { tag: tags.labelName, color: mappedColors.secondary.halcyon },
      ],
    })
  }, [theme, readOnly])
}

export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  placeholder,
  readOnly,
  focusToken,
  initialViewMode = 'code',
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode)
  const editorRef = useRef<ReactCodeMirrorRef>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const [showScrollIndicator, setShowScrollIndicator] = useState(false)
  const codeMirrorTheme = useCodeMirrorTheme(readOnly)

  useEffect(() => {
    if (focusToken === undefined) {
      return
    }
    if (readOnly) {
      return
    }

    setViewMode('code')
    requestAnimationFrame(() => {
      editorRef.current?.view?.focus()
    })
  }, [focusToken, readOnly])

  useEffect(() => {
    const el = scrollAreaRef.current
    if (!el) {
      return
    }

    const updateIndicator = () => {
      const hasOverflow = el.scrollHeight > el.clientHeight
      const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1
      setShowScrollIndicator(hasOverflow && !isAtBottom)
    }

    updateIndicator()
    el.addEventListener('scroll', updateIndicator)
    const observer = new ResizeObserver(updateIndicator)
    observer.observe(el)

    return () => {
      el.removeEventListener('scroll', updateIndicator)
      observer.disconnect()
    }
  }, [viewMode, value])

  const extensions = useMemo(() => [markdown({ codeLanguages: languages }), EditorView.lineWrapping], [])

  return (
    <div className={classNames(styles.container, !readOnly && styles.containerEditable)}>
      <div className={styles.viewToggleFloating}>
        <div className={styles.viewToggleGroup} role="group" aria-label="Editor view mode">
          <button
            type="button"
            className={classNames(
              styles.viewToggleButton,
              readOnly ? styles.viewToggleButtonReadOnly : styles.viewToggleButtonEditable
            )}
            aria-pressed={viewMode === 'code'}
            onClick={() => {
              setViewMode('code')
            }}
          >
            {'</>'}
          </button>
          <button
            type="button"
            className={classNames(
              styles.viewToggleButton,
              readOnly ? styles.viewToggleButtonReadOnly : styles.viewToggleButtonEditable
            )}
            aria-pressed={viewMode === 'preview'}
            onClick={() => {
              setViewMode('preview')
            }}
          >
            Aa
          </button>
        </div>
      </div>

      <div className={styles.editorScrollAreaWrapper}>
        <div
          ref={scrollAreaRef}
          className={classNames(styles.editorScrollArea, !readOnly && styles.editorScrollAreaEditable)}
        >
          {viewMode === 'code' ? (
            <div
              className={classNames(styles.editorRoot, !readOnly && styles.editorRootEditable)}
              onClick={() => {
                if (!readOnly) {
                  editorRef.current?.view?.focus()
                }
              }}
            >
              <CodeMirror
                ref={editorRef}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                editable={!readOnly}
                readOnly={readOnly}
                theme={codeMirrorTheme}
                extensions={extensions}
                basicSetup={{
                  lineNumbers: true,
                  foldGutter: false,
                  highlightActiveLine: false,
                  highlightSelectionMatches: false,
                  autocompletion: false,
                  indentOnInput: true,
                  bracketMatching: true,
                }}
              />
            </div>
          ) : (
            <div className={styles.markdownPreview}>
              <RenderedMarkdown markdown={value} />
            </div>
          )}
        </div>
      </div>
      {showScrollIndicator && (
        <>
          <div className={styles.bottomShadowOverlay} aria-hidden="true" />
          <div className={styles.scrollIndicator}>
            <TooltipWithHover
              trigger={
                <button type="button" aria-label="Scroll for more" className={styles.scrollIndicatorButton}>
                  <CaretDownIcon className={styles.scrollIndicatorIcon} size={20} weight="light" />
                </button>
              }
              side="top"
            >
              Scroll for more
            </TooltipWithHover>
          </div>
        </>
      )}
    </div>
  )
}
